// =============================================
// QUIZ SYSTEM V2
// Comprehensive question bank, adaptive quiz,
// spaced repetition, analytics, gamification
// =============================================

// ===== QUESTION DATABASE =====
// Each question: { id, category, difficulty, question, options, correct, explanation, relatedPart? }

let _nextId = 1;
function q(category, difficulty, question, options, correct, explanation, relatedPart) {
    return { id: _nextId++, category, difficulty, question, options, correct, explanation, relatedPart };
}

export const QUESTIONS = [
    // ========== TOPIC 1: BASIC OPERATIONS & COMPONENTS ==========
    q('basic-ops', 'beginner',
        'In a float-type carburetor system, what creates the low-pressure area that forces fuel to flow into the airstream?',
        ['The throttle valve', 'The venturi', 'The main metering jet', 'The float bowl'],
        1, 'As air flows through the venturi, a low-pressure area is created, which forces the fuel to flow through a main fuel jet located at the throat.', 'venturi'),

    q('basic-ops', 'beginner',
        'What regulates the flow of the fuel/air mixture to the combustion chambers?',
        ['The needle valve', 'The venturi', 'The throttle valve', 'The main metering jet'],
        2, 'The flow of the fuel/air mixture to the combustion chambers is regulated by the throttle valve, which is controlled by the throttle lever or knob in the flight deck.', 'throttle'),

    q('basic-ops', 'beginner',
        'What component regulates the amount of fuel that enters the float chamber?',
        ['Discharge nozzle', 'Float-operated needle valve', 'Throttle valve', 'Accelerator pump'],
        1, 'Fuel is stored in a float chamber, and the amount is controlled by a float-operated needle valve installed in the fuel inlet.', 'needle-valve'),

    q('basic-ops', 'intermediate',
        'What does Bernoulli\'s principle state in relation to fluid flowing through a venturi?',
        ['Fluid speeds up while pressure increases', 'Fluid slows down while pressure increases', 'Fluid speeds up while pressure decreases', 'Fluid temperature drops while velocity remains constant'],
        2, 'Bernoulli\'s principle states that a fluid (gas or liquid) flowing through a venturi speeds up in the restricted area while pressure decreases.', 'venturi'),

    q('basic-ops', 'beginner',
        'What defines "specific fuel consumption" (SFC) for an engine?',
        ['The octane rating of the fuel required', 'The number of pounds of fuel burned per hour to produce one horsepower', 'The maximum gallons per hour burned at full throttle', 'The ratio of fuel to air by volume'],
        1, 'The specific fuel consumption of an engine is the number of pounds of fuel burned per hour to produce one horsepower.'),

    // ========== TOPIC 2: MAIN METERING & IDLING SYSTEMS ==========
    q('main-metering', 'intermediate',
        'What physical property causes fuel to discharge from the nozzle in large intermittent droplets instead of a continuous spray?',
        ['Vapor lock', 'Fuel volatility', 'Air density', 'Surface tension'],
        3, 'Because surface tension causes fuel to adhere to itself and to the walls of the discharge nozzle, the fuel discharges from the nozzle in large intermittent droplets instead of a fine, continuous spray.', 'discharge-nozzle'),

    q('main-metering', 'intermediate',
        'What feature is incorporated into the main metering system to disrupt the effects of surface tension and improve atomization?',
        ['An air bleed system', 'An accelerator well', 'A boost venturi', 'A step-cut rotary valve'],
        0, 'To disrupt the effects of surface tension, an air bleed system is often incorporated in the carburetor design, which decreases fuel surface tension and improves fuel atomization.', 'air-bleed'),

    q('main-metering', 'intermediate',
        'Why is the idle fuel mixture generally set richer than the normal mixture?',
        ['To prevent spark plug fouling', 'Because an engine at idle has little airflow over the cylinders for cooling', 'To increase the air density', 'To prevent carburetor icing'],
        1, 'Because an engine operating at idle has little airflow over the cylinders for cooling, the richer mixture partially offsets this loss.'),

    // ========== TOPIC 3: MIXTURE CONTROL & ECONOMIZERS ==========
    q('mixture-control', 'advanced',
        'How does an Automatic Mixture Control (AMC) adjust the fuel/air mixture during a climb?',
        ['A sealed bellows expands as atmospheric pressure decreases, leaning the mixture', 'A sealed bellows contracts as atmospheric pressure decreases, enriching the mixture', 'A bimetallic spring closes the main metering jet as temperature drops', 'The back-suction valve increases pressure in the float bowl'],
        0, 'As atmospheric pressure decreases (such as during a climb), the bellows expands, which leans the fuel/air mixture.'),

    q('mixture-control', 'advanced',
        'What happens if an Automatic Mixture Control (AMC) sticks in the extended position during descent?',
        ['The engine will stall due to an excessively rich mixture', 'The mixture will become leaner, resulting in a higher cylinder head temperature', 'The accelerator pump will flood the venturi', 'The main metering jet will bypass completely'],
        1, 'If the AMC sticks in the extended position during descent, the mixture will become leaner, resulting in a higher cylinder head temperature.'),

    q('mixture-control', 'intermediate',
        'What is the primary purpose of a power enrichment (economizer) system at high power settings?',
        ['To increase the engine\'s compression ratio', 'To lean the mixture for better fuel economy', 'To provide excess fuel in the mixture to help cool the cylinders', 'To pre-vaporize the fuel before it hits the venturi'],
        2, 'One way to dissipate excess heat is a power enrichment system that provides a richer fuel/air mixture at high power settings; the excess fuel in the mixture helps cool the cylinders.', 'economizer'),

    q('mixture-control', 'advanced',
        'What is the purpose of a back-suction type enrichment system in small carburetors?',
        ['It pumps excess fuel directly into the intake manifold using a diaphragm', 'It varies the pressure in the float chamber between atmospheric pressure and a pressure slightly below atmospheric to control fuel flow', 'It relies on an exhaust gas bleed to pre-heat fuel', 'It pulls fuel backward out of the venturi'],
        1, 'The back-suction mixture control varies the pressure in the float chamber between atmospheric pressure and a pressure slightly below atmospheric using a control valve located in the float chamber vent line.'),

    // ========== TOPIC 4: PRESSURE CARBURETORS & ACCELERATION ==========
    q('pressure-carb', 'intermediate',
        'What occurs if a throttle valve opens rapidly and there is no acceleration system in place?',
        ['A sudden, momentary leaning of the fuel/air mixture, resulting in engine hesitation', 'A sudden enriching of the mixture, causing a backfire', 'The float bowl will overfill', 'Venturi suction will collapse the float'],
        0, 'When a throttle valve opens rapidly, airflow through the carburetor increases before the discharge nozzle provides the corresponding fuel, causing a sudden, momentary leaning of the fuel/air mixture, which results in the engine hesitating.', 'accel-pump'),

    q('pressure-carb', 'intermediate',
        'What acts as the "air metering force" in a pressure-injection carburetor?',
        ['The difference between atmospheric pressure and fuel manifold pressure', 'The differential between impact pressure and venturi suction', 'The pressure from the engine-driven fuel pump', 'Spring tension in the unmetered fuel chamber'],
        1, 'The difference between the impact pressure (chamber A) and venturi suction (chamber B) is referred to as the air metering force, which moves the inner diaphragm in proportion to airflow.'),

    q('pressure-carb', 'advanced',
        'What happens if the diaphragm between chambers A and B breaks in a pressure-injection carburetor?',
        ['Decreased differential, causing the poppet valve to move toward the closed position resulting in a leaner mixture', 'Increased differential, flooding the engine with fuel', 'The idle spring will force the main jet open', 'The AMC will compensate by flooding chamber D'],
        0, 'A broken diaphragm between chambers A and B will result in decreased differential, causing the poppet valve to move toward the closed position and a leaner mixture at all power settings.'),

    q('pressure-carb', 'advanced',
        'In an anti-detonation injection (ADI) system, what mixture is typically sprayed into the carburetor air intake?',
        ['Pure distilled water', 'A mixture of water and ethanol', 'A mixture of water and methanol', 'Aviation gasoline and water'],
        2, 'High powered piston engines used water to spray into the carburetor air intake during maximum power operation; this was usually a mixture of water and methanol.'),

    q('pressure-carb', 'advanced',
        'Unlike a float carburetor, what forces keep the poppet valve open at idle in a pressure-injection carburetor?',
        ['Venturi suction', 'An idle spring in Chamber A', 'Fuel pressure from the boost pump', 'The accelerator well'],
        1, 'At idle speed, there is insufficient airflow through the venturi to actuate the diaphragms, so an idle spring supplements the air metering force to keep the poppet valve open slightly.'),

    q('pressure-carb', 'advanced',
        'What is the specific function of "Chamber D" in a pressure-injection fuel regulator?',
        ['It houses impact air', 'It contains unmetered, regulated fuel pressure', 'It houses venturi suction air', 'It stores metered fuel prior to discharge'],
        1, 'Regulated, unmetered fuel enters Chamber D and presses against the outer diaphragm to close the poppet valve, balancing the air metering force.'),

    // ========== TOPIC 5: TROUBLESHOOTING, MAINTENANCE & ENVIRONMENT ==========
    q('troubleshooting', 'beginner',
        'What atmospheric conditions are most likely to result in the development of carburetor ice?',
        ['Temperatures between 0 and 10°F with low humidity', 'Temperatures between 30 and 40°F and relative humidity exceeding 60 percent', 'Temperatures above 70°F with 100% humidity', 'Any temperature below freezing'],
        1, 'The flight conditions most likely to result in the development of carburetor ice are when the outside air temperature is between 30 and 40 degrees Fahrenheit and the relative humidity exceeds 60 percent.'),

    q('troubleshooting', 'intermediate',
        'Why does carburetor ice restrict airflow and cause the fuel/air mixture to become richer?',
        ['Because ice melts and dilutes fuel', 'Because ice blocks the air bleed', 'Because it restricts the volume of air entering the engine, while fuel flow remains relatively constant', 'Because the float freezes open'],
        2, 'Because carburetor ice restricts airflow, it will cause the fuel air mixture to become richer.'),

    q('troubleshooting', 'intermediate',
        'When correctly adjusting the idle mixture, what should happen immediately after the mixture control is pulled to idle-cutoff?',
        ['Engine RPM should immediately decrease', 'Engine RPM should increase slightly (between 25 and 50 RPM) before dropping off rapidly', 'Engine RPM should surge by over 100 RPM', 'The engine should backfire'],
        1, 'If the idle mixture is adjusted properly, engine r.p.m. will increase slightly (between 25 and 50 r.p.m.) before dropping off rapidly.'),

    q('troubleshooting', 'advanced',
        'If the engine exhibits an excessive rise in RPM at idle cut-off, what is the likely cause?',
        ['A rich idle mixture', 'A lean idle mixture', 'A sticky float', 'Blockage in the bowl vent'],
        0, 'An excessive rise in RPM at idle cut-off indicates a rich idle mixture.'),

    q('troubleshooting', 'advanced',
        'You notice there is NO rise in RPM when leaning at cruise or full power. Which of the following is a LEAN condition cause for this symptom?',
        ['Float level too high', 'Accelerator pump stem packing leak', 'Blockage in the main nozzle', 'Leaky primer'],
        2, 'A condition where there is no rise when leaning at cruise or full power can be caused by a lean condition such as a float level too low, a blockage in the main nozzle, or a blockage in the bowl vent.', 'main-jet'),

    q('troubleshooting', 'intermediate',
        'How should the float level be tested on a flow bench after reassembling a carburetor?',
        ['By blowing compressed air into the vent', 'By attaching a sight glass tube to the carburetor fuel drain port and measuring the fuel surface relative to the parting surface', 'By weighing the float bowl', 'By listening for the needle valve click'],
        1, 'Attach a sight glass tube to the carburetor fuel drain port, attach a fuel supply at the correct pressure, and measure the distance from the fuel surface in the glass tube to the parting surface mark.', 'float'),

    // ========== FUEL CHARACTERISTICS & PHYSICAL PROPERTIES ==========
    q('fuel-properties', 'beginner',
        'What is the definition of "volatility" in aviation fuel?',
        ['The fuel\'s weight per gallon', 'The fuel\'s ability to resist detonation', 'The tendency of a liquid to vaporize', 'The fuel\'s surface tension'],
        2, 'Volatility determines how readily a fuel changes from a liquid to a vapor, which is critical for cold starting and preventing vapor lock.'),

    q('fuel-properties', 'beginner',
        'Which color code identifies 100LL (Low Lead) aviation gasoline?',
        ['Red', 'Green', 'Blue', 'Colorless'],
        2, '100LL is dyed blue to easily distinguish it from other fuel grades.'),

    q('fuel-properties', 'beginner',
        'What does an octane rating measure?',
        ['The energy content of the fuel', 'The anti-knock properties or resistance to detonation', 'The vapor pressure of the fuel', 'The lead content'],
        1, 'The octane number compares the anti-knock properties of a fuel to a mixture of iso-octane and normal heptane.'),

    q('fuel-properties', 'intermediate',
        'How much does one U.S. gallon of aviation gasoline (Avgas) weigh under standard conditions?',
        ['5.5 lbs', '6.0 lbs', '6.7 lbs', '7.2 lbs'],
        1, 'One U.S. gallon of standard Avgas weighs exactly 6 pounds, a vital metric for calculating weight and balance and specific fuel consumption.'),

    q('fuel-properties', 'intermediate',
        'What is the stoichiometric (perfect) air-to-fuel mixture ratio by weight for an aircraft engine?',
        ['10:1', '12:1', '15:1', '18:1'],
        2, 'A stoichiometric mixture is 15 parts of air to 1 part of fuel by weight, though engines usually run slightly richer for cooling.'),

    // ========== FLOAT CARBURETOR INTERNALS ==========
    q('float-internals', 'beginner',
        'In a float carburetor, where is the float chamber vented to?',
        ['The intake manifold', 'Ambient atmospheric pressure', 'The discharge nozzle', 'The accelerator pump well'],
        1, 'The float chamber is vented to ambient pressure so that atmospheric pressure can push fuel through the main metering jet when low pressure forms in the venturi.', 'float-bowl'),

    q('float-internals', 'beginner',
        'What happens to the float and needle valve as fuel is consumed by the engine?',
        ['The float rises, closing the needle valve', 'The float drops, opening the needle valve', 'The float expands', 'The needle valve seals completely'],
        1, 'As fuel flows out to the jet, the fuel level drops, causing the float to drop and open the needle valve to let more fuel in from the tanks.', 'float'),

    q('float-internals', 'intermediate',
        'What component is used to check the side clearance between the float and the float bowl during an overhaul?',
        ['A micrometer', 'A specialized test fixture and drill rod', 'A depth gauge', 'A feeler gauge'],
        1, 'A special test fixture is used to pass a drill rod of a specified size completely around the float to ensure it won\'t bind against the bowl walls.'),

    q('float-internals', 'intermediate',
        'What happens if the air bleed hole in an idle tube becomes clogged?',
        ['The engine will run excessively lean at idle', 'The engine will run excessively rich at idle', 'The main discharge nozzle will take over', 'The float chamber will overfill'],
        1, 'A clogged air bleed restricts the air mixing with the fuel, causing the idle emulsion to become overly rich.', 'idle-air-bleed'),

    q('float-internals', 'advanced',
        'When measuring the float level using a flow bench, where should the measurement be taken in relation to the glass tube?',
        ['At the edge of the glass where the meniscus forms', 'At the exact center of the glass tube', 'From the top of the glass tube to the parting surface', 'From the bottom of the float to the fuel level'],
        1, 'You must measure the distance from the fuel surface in the center of the glass tube to the parting surface mark to minimize the deceptive effects of surface tension at the edges.'),

    // ========== DIAGNOSTICS & TROUBLESHOOTING ==========
    q('diagnostics', 'beginner',
        'If an engine has a leaky manual primer system, how will this affect the carburetor\'s fuel/air mixture?',
        ['It will create a dangerously lean mixture', 'It will create an excessively rich mixture', 'It will have no effect once the engine is running', 'It will cause immediate vapor lock'],
        1, 'A leaky primer allows unmetered fuel to bypass the carburetor and get sucked directly into the intake manifold, resulting in a rich running condition.'),

    q('diagnostics', 'intermediate',
        'You observe fuel leaking from the carburetor throat shortly after engine shutdown. What is the most likely internal cause?',
        ['A clogged main metering jet', 'A worn needle valve or seat in the float chamber', 'A broken accelerator pump linkage', 'An induction leak'],
        1, 'If the float needle valve fails to seat properly, fuel from the gravity feed or boost pump will overfill the bowl and spill out the discharge nozzle.', 'needle-valve'),

    q('diagnostics', 'intermediate',
        'An engine experiences hesitation and a momentary stumble when the throttle is rapidly advanced. Which carburetor circuit is likely failing?',
        ['The idle circuit', 'The economizer valve', 'The accelerator pump', 'The automatic mixture control (AMC)'],
        2, 'When the throttle is opened rapidly, airflow increases instantly, but fuel lags. The accelerator pump is required to shoot a supplemental stream of fuel to prevent a lean stumble.', 'accel-pump'),

    q('diagnostics', 'advanced',
        'During a ground run up, an engine shows high EGT and high CHT readings, coupled with low fuel consumption. What does this indicate?',
        ['A rich idle mixture', 'A leaky primer', 'An overall lean running condition', 'A punctured carburetor float'],
        2, 'A lean mixture burns slower and hotter, transferring more heat into the cylinder heads and exhaust, resulting in high CHT and EGT readouts.'),

    q('diagnostics', 'advanced',
        'You notice there is NO rise in RPM when pulling the mixture to idle-cutoff. What is a likely cause?',
        ['An intake manifold air leak', 'An excessively rich idle adjustment', 'A ruptured accelerator pump boot', 'A stuck economizer'],
        0, 'A lack of RPM rise at idle cutoff indicates a lean condition. An intake manifold leak draws in unmetered air, constantly leaning the mixture out before cutoff is even applied.'),

    // ========== ORIGINAL QUESTIONS (KEPT & CATEGORIZED) ==========

    // --- Carburetor Components ---
    q('carb-parts', 'intermediate',
        'What component in a float-type carburetor is primarily responsible for metering fuel flow during cruise power settings?',
        ['Idle jet', 'Main metering jet', 'Accelerator pump', 'Economizer valve'],
        1, 'The main metering jet is a precision orifice that controls the rate of fuel flow from the float bowl to the main discharge nozzle. It is the primary metering device from about quarter-throttle through full power.', 'main-jet'),

    q('carb-parts', 'intermediate',
        'What is the purpose of the air bleed in the main fuel circuit?',
        ['To increase fuel flow at high power', 'To create a fuel-air emulsion for better atomization and provide mixture compensation', 'To cool the fuel before it enters the venturi', 'To prevent fuel from flowing at idle'],
        1, 'The air bleed introduces air into the fuel passage, creating an emulsion that atomizes better at the nozzle. It also compensates for the tendency of the mixture to go rich at higher airspeeds.', 'air-bleed'),

    q('carb-parts', 'intermediate',
        'What happens if the float in the carburetor develops a leak and becomes fuel-soaked (heavy)?',
        ['The needle valve stays closed, causing fuel starvation', 'The float sits lower, raising the fuel level — mixture becomes chronically rich', 'The engine runs lean at all power settings', 'No effect — the float still works normally'],
        1, 'A heavy, leaking float sits lower in the bowl because it is less buoyant. This means the needle valve opens more or stays open longer than intended, causing the fuel level to rise above normal.', 'float'),

    q('carb-parts', 'intermediate',
        'What drives fuel flow from the float bowl through the main discharge nozzle during cruise?',
        ['The engine-driven fuel pump pushes fuel through the jets', 'The pressure difference between atmospheric pressure on the bowl and reduced pressure at the venturi throat', 'Gravity pulls fuel downward into the engine', 'The accelerator pump provides continuous pressure'],
        1, 'The float bowl is vented to atmospheric pressure. The venturi creates a low-pressure zone at its throat. This pressure difference is what drives fuel from the bowl, through the main jet, and out the discharge nozzle.', 'venturi'),

    q('carb-parts', 'intermediate',
        'What is the function of progression (transition) holes in a carburetor?',
        ['They provide additional ventilation to the float bowl', 'They drain excess fuel from the venturi', 'They provide smooth fuel delivery during the transition from idle to main metering system', 'They act as overflow ports for the accelerator pump'],
        2, 'Progression holes are positioned between the idle port and the main discharge nozzle. As the throttle opens from idle, these holes are progressively exposed to intake vacuum, providing a gradually increasing fuel supply.'),

    q('carb-parts', 'intermediate',
        'What is the purpose of the inlet check valve in the accelerator pump circuit?',
        ['To regulate fuel pressure to the pump', 'To prevent fuel from being pushed back into the bowl when the pump charges, and to allow fuel to refill the pump on the return stroke', 'To meter the amount of fuel the pump delivers', 'To filter debris from the fuel'],
        1, 'The inlet check valve is a one-way valve that closes when the pump plunger pushes down. On the return stroke, the valve opens to allow fuel from the bowl to refill the pump chamber.', 'accel-pump'),

    q('carb-parts', 'intermediate',
        'In a float-type carburetor, what prevents fuel from siphoning out of the main discharge nozzle when the engine is not running?',
        ['The throttle plate seals off the nozzle', 'A check valve at the nozzle tip', 'The tip of the nozzle is positioned above the fuel level in the bowl — fuel cannot flow uphill without suction', 'The mixture control valve blocks all fuel flow'],
        2, 'The main discharge nozzle tip is deliberately positioned several millimeters above the static fuel level in the bowl. Without the venturi suction to overcome this height difference, fuel cannot climb up.', 'discharge-nozzle'),

    q('carb-parts', 'advanced',
        'What is the "back-suction" type economizer and how does it work?',
        ['It pumps fuel backward into the tank at cruise', 'At part throttle, manifold vacuum is applied to the fuel passage to reduce fuel flow (leaner); at full throttle, vacuum drops, allowing enrichment', 'It creates suction at the back of the carburetor for cooling', 'It reverses airflow through the carburetor at idle'],
        1, 'The back-suction economizer uses manifold vacuum to regulate enrichment. A passage connects the bowl vent to the intake manifold downstream of the throttle.', 'economizer'),

    // --- Fuel System ---
    q('fuel-system', 'intermediate',
        'What is the difference between fuel starvation and fuel exhaustion?',
        ['They mean the same thing', 'Fuel starvation: fuel onboard but cannot reach engine. Fuel exhaustion: tanks are empty.', 'Fuel exhaustion is more dangerous than fuel starvation', 'Fuel starvation only happens on the ground'],
        1, 'Fuel starvation means fuel is present in the tanks but cannot reach the engine. Fuel exhaustion means the aircraft has genuinely run out of fuel.'),

    q('fuel-system', 'beginner',
        'Why must fuel tanks be vented?',
        ['To allow fuel vapors to escape for safety', 'To allow air to replace consumed fuel, preventing vacuum that would stop fuel flow', 'To cool the fuel in the tank', 'To equalize fuel levels between tanks'],
        1, 'As fuel is consumed, the volume in the tank decreases. Without a vent to admit air, a vacuum would develop, eventually opposing fuel flow and starving the engine.'),

    q('fuel-system', 'beginner',
        'What is the purpose of the gascolator in the fuel system?',
        ['To measure fuel flow rate', 'To warm the fuel before it reaches the carburetor', 'To strain particles and collect water/sediment at a low point for draining', 'To regulate fuel pressure'],
        2, 'The gascolator is a combination strainer and sediment trap at the lowest point in the fuel system before the engine.'),

    q('fuel-system', 'intermediate',
        'When should the electric boost pump typically be used?',
        ['Only during cruise for fuel economy', 'During engine start, takeoff, landing, tank switching, and as backup for engine-pump failure', 'Only during emergencies', 'Continuously during all flight phases'],
        1, 'The boost pump serves as a backup and assists during critical phases: starting, takeoff and landing, switching tanks, and as an emergency backup if the engine-driven pump fails.'),

    q('fuel-system', 'beginner',
        'What color is 100LL aviation gasoline (AVGAS)?',
        ['Red', 'Clear/straw', 'Blue', 'Green'],
        2, '100LL (Low Lead) AVGAS is dyed blue to distinguish it from other fuel grades.'),

    q('fuel-system', 'intermediate',
        'What is vapor lock and when is it most likely to occur?',
        ['When ice blocks the fuel line — most likely in winter', 'When fuel vaporizes in the lines due to heat and low pressure, blocking liquid fuel flow — most likely on hot days', 'When the fuel cap is locked and cannot be opened', 'When water freezes in the fuel filter'],
        1, 'Vapor lock occurs when fuel in the lines absorbs enough heat to boil (vaporize), forming gas bubbles that block liquid flow.'),

    q('fuel-system', 'beginner',
        'What is the primary purpose of the engine-driven fuel pump?',
        ['To pressurize the fuel for fuel injection', 'To deliver fuel from the tanks to the carburetor at the required pressure and flow rate', 'To return excess fuel to the tanks', 'To measure fuel flow for the gauge'],
        1, 'The engine-driven mechanical fuel pump draws fuel from the tanks and delivers it to the carburetor at the specified pressure and flow rate.'),

    q('fuel-system', 'beginner',
        'Why should fuel tanks be filled after the last flight of the day?',
        ['To save time in the morning', 'To minimize empty airspace in the tank where moisture can condense overnight, reducing water contamination', 'To keep the fuel warm', 'FAA regulation requires it'],
        1, 'A full tank has minimal air space, greatly reducing condensation. This is one of the most effective ways to prevent water contamination.'),

    q('fuel-system', 'intermediate',
        'What happens to fuel metering if the carburetor bowl vent line becomes partially blocked by debris?',
        ['No effect on fuel metering', 'The mixture runs richer because more fuel is pushed out of the bowl', 'The mixture runs leaner because reduced bowl pressure means less fuel is pushed to the nozzle', 'The engine immediately shuts down'],
        2, 'If the vent is partially blocked, the pressure above the fuel drops as fuel is consumed. This reduces the pressure differential between bowl and venturi, resulting in less fuel flow — a leaner mixture.'),

    // --- Mixture & Metering ---
    q('mixture', 'intermediate',
        'As you lean the mixture from full rich during cruise, what happens to EGT?',
        ['EGT decreases continuously', 'EGT rises to a peak, then decreases if leaned further past peak', 'EGT stays constant', 'EGT drops initially then rises'],
        1, 'As mixture is leaned, combustion efficiency increases and EGT rises — until peak EGT, which corresponds approximately to best economy mixture.'),

    q('mixture', 'intermediate',
        'Why is the mixture enriched for full-power operations like takeoff?',
        ['To save fuel', 'To reduce noise', 'To provide cooling and a safety margin against detonation', 'Because the engine cannot burn lean mixture at high RPM'],
        2, 'A richer-than-stoichiometric mixture at high power provides two benefits: the extra fuel absorbs heat, and it provides a safety margin against detonation.'),

    q('mixture', 'intermediate',
        'What does the economizer (power enrichment) valve do?',
        ['It leans the mixture at cruise to save fuel', 'It provides additional fuel at full throttle for cooling and detonation protection', 'It adjusts the idle mixture', 'It controls the mixture on the ground only'],
        1, 'The economizer valve opens at or near full throttle, allowing additional fuel to flow to the engine beyond what the main jet provides.', 'economizer'),

    q('mixture', 'intermediate',
        'Based on spark plug appearance, what does a white, blistered electrode indicate?',
        ['Normal operation', 'Rich mixture operation', 'Lean mixture operation causing excessive heat', 'Oil contamination'],
        2, 'White, blistered spark plug electrodes indicate the plug has been exposed to excessive heat — a hallmark of lean mixture operation.'),

    q('mixture', 'intermediate',
        'At 8,000 feet density altitude, what happens to the mixture if no leaning is performed?',
        ['The mixture stays the same as at sea level', 'The mixture becomes richer because thinner air means less air mass but the same fuel flow', 'The mixture becomes leaner because fuel pressure decreases with altitude', 'The mixture auto-adjusts to compensate'],
        1, 'As altitude increases, air density decreases. The carburetor jets are sized for sea-level air density, so at altitude the same amount of fuel is metered but there is less air.'),

    q('mixture', 'intermediate',
        'What is stoichiometric ratio for gasoline combustion and what does it mean?',
        ['12:1 — 12 parts fuel to 1 part air for perfect combustion', '15:1 — 15 parts exhaust to 1 part fuel', 'Approximately 15:1 by weight — 15 parts air to 1 part fuel for chemically complete combustion', '20:1 — 20 parts air to 1 part fuel'],
        2, 'The stoichiometric ratio for gasoline is approximately 14.7:1 to 15:1 air to fuel by weight. At this ratio, all the fuel and all the oxygen are theoretically consumed in combustion.'),

    q('mixture', 'intermediate',
        'Why should the mixture be set to Full Rich before descending from cruise altitude?',
        ['To save fuel during descent', 'To prevent the engine from running too lean as denser air is encountered at lower altitudes, which could cause overheating or power loss', 'To reduce noise', 'To improve fuel economy at lower altitude'],
        1, 'During descent, air density increases. If the mixture remains leaned for cruise altitude, the increasing air density causes the mixture to go progressively leaner.'),

    q('mixture', 'intermediate',
        'What is "best power" mixture and where does it fall relative to peak EGT?',
        ['At peak EGT exactly', 'Approximately 100-150°F rich of peak EGT', 'Approximately 100°F lean of peak EGT', 'Full rich is always best power'],
        1, 'Best power mixture produces maximum engine output and occurs approximately 100-150°F rich of peak EGT (around 12-12.5:1 air-fuel ratio).'),

    q('mixture', 'advanced',
        'What is detonation and why is it dangerous?',
        ['Normal combustion — it is not dangerous', 'Pre-ignition caused by hot spots in the cylinder', 'Explosive, uncontrolled combustion of the remaining fuel charge after normal ignition — causes extreme pressure spikes that damage pistons, valves, and bearings', 'Combustion that occurs after the exhaust valve opens'],
        2, 'Detonation is the spontaneous, explosive combustion of the end gases in the cylinder after normal spark ignition has started the flame front.'),

    // --- Troubleshooting (Original) ---
    q('troubleshooting', 'intermediate',
        'An engine stumbles momentarily when the throttle is advanced rapidly from idle, but runs fine when throttle is advanced slowly. What is the most likely cause?',
        ['Blocked main jet', 'Failed or worn accelerator pump', 'Stuck float', 'Clogged fuel tank vent'],
        1, 'The accelerator pump provides a supplementary shot of fuel during rapid throttle opening to prevent a momentary lean condition.', 'accel-pump'),

    q('troubleshooting', 'intermediate',
        'An engine runs normally at idle and low cruise, but loses power and runs rough at high power settings. Fuel pressure gauge reads low. What area should you investigate first?',
        ['Idle circuit in the carburetor', 'Fuel supply: engine-driven pump, fuel filters, fuel line restriction, or tank venting', 'Spark plug gap settings', 'Carburetor heat valve position'],
        1, 'Loss of power at high settings with low fuel pressure points to a fuel supply problem — the system cannot deliver enough fuel at the high demand of full power.'),

    q('troubleshooting', 'beginner',
        'During pre-flight fuel drain, you find clear droplets sitting below the colored fuel in the sample cup. What have you found?',
        ['Normal fuel separation — ignore it', 'Water contamination in the fuel', 'Oil mixed with fuel', 'Air bubbles'],
        1, 'Water is denser than aviation gasoline and immiscible with it, so it settles to the bottom of the sample.'),

    q('troubleshooting', 'intermediate',
        'Fuel is seen dripping from the carburetor drain, black smoke comes from the exhaust, and the engine runs very rough. What is the most likely carburetor fault?',
        ['Blocked main jet', 'Worn throttle shaft bushings', 'Float stuck in the down position or needle valve not sealing — carburetor flooding', 'Economizer valve stuck closed'],
        2, 'Fuel dripping from the carb, black smoke (rich mixture), and rough running all point to a flooding condition.', 'float'),

    q('troubleshooting', 'advanced',
        'An engine runs rough at idle but smooths out as RPM increases. Idle mixture check shows the mixture is correct. What should you check next?',
        ['Main metering jet', 'Accelerator pump', 'Throttle shaft bushings for wear (air leak)', 'Economizer valve'],
        2, 'Worn throttle shaft bushings allow unmetered air to leak into the induction system. At idle, the total airflow is small, so this leaked air represents a significant percentage.', 'throttle'),

    q('troubleshooting', 'advanced',
        'After an overhaul, a carburetor is installed and the engine starts but immediately dies when the mixture is moved from full rich toward lean. What is the most likely issue?',
        ['Main jet is too large', 'Float level is set too high', 'Mixture control rigging is backwards (full rich position is actually lean)', 'Idle speed set too high'],
        2, 'If the mixture cable or linkage is rigged backwards, "full rich" at the cockpit control actually moves the mixture valve toward cutoff in the carb.'),

    q('troubleshooting', 'intermediate',
        'An engine develops excessive fuel consumption and sooty exhaust at all power settings. The mixture control operates normally through its full range. What should you inspect?',
        ['Idle jet for blockage', 'Float level — likely too high, allowing excessive fuel into the bowl', 'Throttle shaft for wear', 'Air filter for blockage'],
        1, 'A float level that is too high means the fuel surface is closer to the discharge nozzle tip, requiring less venturi suction to draw fuel.', 'float'),

    q('troubleshooting', 'intermediate',
        'During a magneto check, the engine drops more than the allowable RPM on one magneto. What should you attempt before assuming a magneto problem?',
        ['Shut down and inspect the magnetos', 'Lean the mixture aggressively and re-check, as fouled plugs from rich operation may clear', 'Switch to the other magneto permanently', 'Increase idle speed'],
        1, 'A common cause of excessive mag drop is carbon-fouled spark plugs from extended ground operations at rich mixture.'),

    q('troubleshooting', 'intermediate',
        'Engine EGT is abnormally high on all cylinders during cruise. What is the most likely cause?',
        ['Mixture too rich', 'Mixture too lean (approaching or at peak EGT)', 'Exhaust leak before the EGT probes', 'Defective EGT gauge'],
        1, 'High EGT across all cylinders indicates the mixture is at or near peak EGT — the point of maximum combustion efficiency.'),

    q('troubleshooting', 'advanced',
        'A pilot reports the engine surges (RPM oscillates) during cruise flight. Mixture adjustments do not stop the surging. What carburetor component is the most likely culprit?',
        ['Accelerator pump', 'Float or needle valve — float bouncing or needle valve sticking intermittently', 'Idle mixture screw', 'Air bleed jets'],
        1, 'Engine surging (rhythmic power fluctuations) at cruise is classic float-bowl fuel level oscillation.', 'float'),

    q('troubleshooting', 'beginner',
        'After maintenance, an engine will not start. The engine cranks, has spark, and fuel is present at the carburetor inlet. What simple item is frequently overlooked?',
        ['The engine needs an overhaul', 'The fuel selector valve may be OFF or on an empty tank', 'The mixture control may be at Idle Cutoff', 'The magnetos need to be re-timed'],
        2, 'The mixture must be at Full Rich for starting. If it is left at Idle Cutoff — the engine will crank with spark and fuel pressure but never start.'),

    // --- Operations & Processes ---
    q('processes', 'intermediate',
        'Why does applying carburetor heat cause a slight drop in engine RPM/power?',
        ['Hot air melts the fuel, reducing combustion efficiency', 'Hot air is less dense than cold air, so less air mass enters the engine per cycle', 'The carb heat valve blocks some airflow', 'Hot air causes the fuel to evaporate before reaching the cylinders'],
        1, 'Heated air is less dense than ambient air. When carb heat diverts warmer air to the carburetor, each intake stroke draws in less air mass.'),

    q('processes', 'intermediate',
        'In what ambient temperature range is carburetor icing MOST likely to occur?',
        ['Below 0°F (-18°C) only', 'Between about 20°F and 70°F (-7°C to 21°C) with visible moisture or high humidity', 'Only above 90°F (32°C)', 'Carburetor icing cannot occur above freezing'],
        1, 'Carburetor icing is most likely in the 20-70°F range with high humidity because the temperature drop from fuel evaporation and venturi effect can bring the carburetor temperature well below freezing.'),

    q('processes', 'intermediate',
        'What would happen if the atmospheric vent to the float bowl became completely blocked during flight?',
        ['Nothing — the vent is not important during flight', 'The mixture would gradually go lean as fuel is consumed and a vacuum develops in the bowl', 'The mixture would go rich', 'The engine would immediately stop'],
        1, 'With the vent blocked, as fuel is consumed from the bowl, air cannot enter to replace it. A partial vacuum develops in the bowl, reducing the pressure difference between bowl and venturi.', 'float-bowl'),

    q('processes', 'intermediate',
        'Which type of carburetor icing can occur at ambient temperatures as high as 100°F (38°C)?',
        ['Impact ice', 'Throttle ice (evaporative icing)', 'Structural ice', 'Induction filter ice'],
        1, 'Throttle/evaporative icing results from the cooling effect of fuel vaporization combined with the venturi pressure drop — these can drop the carburetor temperature 60-70°F below ambient.'),

    q('processes', 'beginner',
        'What is the first indication of carburetor icing in an aircraft with a fixed-pitch propeller?',
        ['Increase in RPM', 'Decrease in RPM', 'Increase in oil temperature', 'Decrease in fuel flow'],
        1, 'In a fixed-pitch propeller installation, RPM is a direct indicator of power output. As carb ice forms, it restricts airflow through the carburetor, reducing power — and RPM drops.'),

    q('processes', 'intermediate',
        'After applying full carburetor heat to clear suspected icing, you notice a further RPM drop followed by a gradual RPM increase. What does this indicate?',
        ['Carburetor heat valve is stuck', 'Ice was present and is melting — the initial drop is from the hot (less dense) air, the rise is from cleared ice', 'The engine is overheating', 'There was no ice present'],
        1, 'The initial RPM drop after applying carb heat is normal — hot air is less dense, so power drops slightly. If ice was present, as it clears, airflow is restored and RPM rises.'),

    q('processes', 'intermediate',
        'Where in the carburetor does throttle ice typically form?',
        ['In the float bowl', 'On and around the throttle plate where the greatest temperature drop occurs', 'In the fuel inlet line', 'On the air filter'],
        1, 'The throttle plate area experiences the greatest temperature drop because of two combined effects: fuel evaporation and the venturi/restricted-passage pressure drop.', 'throttle'),

    q('processes', 'intermediate',
        'Why is carburetor icing more likely at low-power settings (approach/descent) than at full power?',
        ['More fuel is being used at low power', 'The throttle plate is nearly closed, creating a greater pressure drop and more cooling across it', 'The engine runs hotter at low power', 'The fuel is colder at low power settings'],
        1, 'At low power, the throttle plate is nearly closed, creating a very high pressure drop (and thus temperature drop) across the small throttle opening.', 'throttle'),

    // ========== TOPIC: ATMOSPHERIC GAS LAWS & POWERPLANT ALGEBRA ==========
    q('gas-laws', 'intermediate',
        'According to Boyle\'s Law, if the volume of a dry gas is compressed to half its original size while the temperature remains constant, what happens to its pressure?',
        ['It remains the same', 'It doubles', 'It is reduced by half', 'It quadruples'],
        1, 'Boyle\'s Law states that volume and pressure are inversely proportional (P1V1 = P2V2). Halving the volume doubles the pressure — a critical concept for understanding manifold pressure in turbocharged induction systems.'),

    q('gas-laws', 'advanced',
        'You are calculating the Specific Fuel Consumption (SFC) for an engine producing 160 horsepower. If the engine burns 12 gallons of 100LL Avgas per hour, what is the SFC? (100LL weighs 6 lbs/gallon)',
        ['0.45 lbs/hp/hr', '0.52 lbs/hp/hr', '0.60 lbs/hp/hr', '0.75 lbs/hp/hr'],
        0, 'First, convert gallons to pounds: 12 gallons × 6 lbs = 72 lbs of fuel per hour. Then, divide by horsepower: 72 lbs ÷ 160 hp = 0.45 pounds of fuel burned per hour to produce one horsepower.'),

    q('gas-laws', 'intermediate',
        'Charles\'s Law relates to carburetor icing and induction systems. It states that if pressure is constant, the volume of a gas is directly proportional to its:',
        ['Humidity', 'Absolute Temperature', 'Density', 'Velocity'],
        1, 'As air expands (increases in volume) going through the venturi, its absolute temperature drops proportionally — this is the primary physical catalyst for carburetor ice formation.'),

    q('gas-laws', 'advanced',
        'If a venturi throat is reduced to one-third (1/3) of the area of the air inlet, what happens to the velocity of the air passing through it?',
        ['It decreases by a factor of 3', 'It remains constant but pressure drops', 'It increases by a factor of 3', 'It squares'],
        2, 'By the continuity equation (A₁V₁ = A₂V₂), if the cross-sectional area decreases to 1/3, the velocity must increase by a factor of 3 to allow the same mass of air to flow through.', 'venturi'),

    q('gas-laws', 'intermediate',
        'Dalton\'s Law of Partial Pressures states that the total pressure of a gas mixture equals the sum of the partial pressures of its individual components. How does this relate to carburetor icing?',
        ['Higher humidity means more water vapor displacing dry air, reducing oxygen for combustion', 'It determines the octane rating of the fuel', 'It explains why fuel burns faster at altitude', 'It has no relation to carburetor icing'],
        0, 'The partial pressure of water vapor in the air is a key factor. High humidity means the air already contains significant moisture, which condenses and freezes as the temperature drops through the venturi and around the throttle plate.'),

    q('gas-laws', 'beginner',
        'What is the stoichiometric air-fuel ratio by weight for aviation gasoline?',
        ['10:1', '12:1', '15:1', '20:1'],
        2, 'The chemically perfect ratio for avgas is approximately 15 parts air to 1 part fuel by weight. At this ratio, all fuel and all available oxygen are theoretically consumed during combustion.'),

    q('gas-laws', 'advanced',
        'An engine produces 200 HP at sea level. At 10,000 feet (where air density is approximately 74% of sea level), what is the approximate naturally-aspirated power output, assuming all other factors remain constant?',
        ['200 HP', '148 HP', '100 HP', '74 HP'],
        1, 'Power output is roughly proportional to air density. At 74% density: 200 HP × 0.74 = 148 HP. This is why mixture must be leaned at altitude — the carburetor still tries to meter fuel for the original airflow rate.'),

    // ========== TOPIC: HARDWARE SPECIFICS (Marvel-Schebler / MSA) ==========
    q('hardware-specifics', 'intermediate',
        'What is the fundamental design difference between the MSA MA-4SPA and the HA-6 carburetor models?',
        ['The MA-4SPA is pressure-injected, the HA-6 is a float type', 'The MA-4SPA is an updraft carburetor, the HA-6 is a side-draft carburetor', 'The HA-6 lacks an accelerator pump', 'The MA-4SPA uses a water injection port'],
        1, 'The MA series (like the 4SPA) are traditional updraft plain-tube carburetors mounted under the engine, whereas the HA-6 is a side-draft design used on specific engine cowlings where vertical space is limited.'),

    q('hardware-specifics', 'advanced',
        'You are troubleshooting an engine with an MA-4SPA carburetor. The pilot reports the engine hesitates and almost quits during deceleration (throttling back). According to the MSA troubleshooting chart, what is the primary suspect?',
        ['A clogged main metering jet', 'A defective accelerator pump leather boot', 'A sunken composite float', 'A stuck automatic mixture control'],
        1, 'A worn or folded leather boot on the accelerator pump plunger allows fuel to get to the wrong side of the pump cavity. When throttled back, this trapped fuel is forced out the vent, causing a temporary rich stumble or stall.'),

    q('hardware-specifics', 'intermediate',
        'What type of main jets are utilized in the MA-3 and MA-4 series carburetors?',
        ['Variable, pilot-adjustable jets', 'Electronically controlled pintle valves', 'Plain tube, fixed jets', 'Stepped metering pins'],
        2, 'These carburetors utilize plain tube, fixed jet type. The main metering orifice size is physically drilled and calibrated at the factory and cannot be adjusted in the field.'),

    q('hardware-specifics', 'advanced',
        'During an overhaul of an MA-4-5 carburetor, what must be done to ensure a clean, positive shut-down when the pilot pulls the mixture control?',
        ['The idle cutoff valve must be lapped to its seat', 'The primer lines must be purged', 'The throttle valve must be set to fully close against the venturi wall', 'The float level must be lowered'],
        0, 'The mixture control rotary valve or plunger must perfectly seal the fuel passage in the idle cut-off position. If it doesn\'t seat perfectly, fuel will continue to seep in, and the engine will continue to run or "diesel" after shutdown.'),

    q('hardware-specifics', 'intermediate',
        'On a Marvel-Schebler MA-4SPA carburetor, what does the "SPA" designation indicate?',
        ['Single Point, Automatic — it has a single-point fuel injection', 'It has a Swirl Power Augmenter for better atomization', 'It includes an integrated Spark Plug Adapter', 'It was designed for use with supercharged engines only'],
        0, 'The SPA designation on Marvel-Schebler carburetors indicates specific design refinements for single-point fuel metering with automatic mixture features built into the base model.'),

    q('hardware-specifics', 'advanced',
        'What is the correct procedure for checking float level on an MA-4SPA during overhaul?',
        ['Measure fuel flow at idle and compare to specifications', 'Invert the upper body and measure the distance from the gasket surface to the highest point of the float with a T-scale', 'Connect the carburetor to a test engine and measure fuel pressure', 'Weigh the float assembly and compare to the manufacturer\'s weight limit'],
        1, 'Float level is checked with the upper carburetor body inverted. A T-scale or depth gauge measures the distance from the gasket mating surface to the highest point of the float. This dimension must fall within the manufacturer\'s specified tolerance.'),

    q('hardware-specifics', 'intermediate',
        'The idle mixture adjustment screw on a typical MA-series carburetor controls:',
        ['The total amount of air entering the engine at idle', 'The amount of fuel that flows through the idle jet', 'The position of the throttle butterfly at idle', 'The pressure in the float bowl'],
        1, 'The idle mixture screw is a needle valve that restricts or opens the idle fuel passage. Turning it clockwise (in) leans the idle mixture by restricting fuel; counter-clockwise (out) richens it. It does NOT control air.'),

    q('accelerator-pump', 'intermediate',
        'In a typical float carburetor, what is the primary purpose of the leather or rubber plunger in the accelerator pump?',
        ['To regulate the float bowl pressure', 'To force a measured quantity of fuel into the airstream when the throttle is opened quickly', 'To prevent fuel from flowing back into the main tank', 'To pulsate the needle valve for better seating'],
        1, 'The accelerator pump uses a plunger to force an extra shot of fuel into the venturi during rapid throttle advancement, preventing a lean-out hesitation.', 'accel-pump'),

    q('economizer', 'advanced',
        'Why must a power enrichment (economizer) system provide extra fuel at full throttle settings regardless of fuel economy?',
        ['To increase the octane of the fuel-air mixture', 'To provide liquid cooling for the cylinders via the latent heat of evaporation', 'To compensate for the lack of ram-air at high altitudes', 'To lubricate the intake valves'],
        1, 'At high power, the excess fuel in the rich mixture absorbs heat as it vaporizes, helping to cool the internal engine components and prevent detonation.'),

    // ========== AS2520 COMPREHENSIVE EXAM — 100 QUESTIONS ==========
    q('gas-laws', 'intermediate',
        'According to Boyle\'s Law, if the volume of a gas is halved while temperature remains constant, the pressure will:',
        ['Decrease by half', 'Remain the same', 'Double', 'Quadruple'],
        2, 'Boyle\'s Law: P₁V₁ = P₂V₂ at constant temperature. Halving volume doubles pressure.'),

    q('fuel-system', 'advanced',
        'In an RSA fuel injection system, a leak in the impact air line between the venturi and the fuel control unit will result in:',
        ['An excessively rich mixture at high power', 'An excessively lean mixture at all power settings', 'Engine overspeed at idle', 'No effect on the fuel-air ratio'],
        1, 'A leak in the impact air line reduces the air metering force, causing less fuel to be delivered — resulting in a lean mixture.'),

    q('fuel-system', 'intermediate',
        'Which component in a pressure-feed fuel system is responsible for maintaining a constant pressure to the carburetor inlet?',
        ['Engine-driven pump relief valve', 'Centrifugal boost pump', 'Float needle valve', 'Fuel selector valve'],
        0, 'The engine-driven pump has a built-in relief valve that maintains a constant fuel pressure to the carburetor inlet.'),

    q('basic-ops', 'beginner',
        'What is the chemical fuel-to-air ratio for "Stoichiometric" combustion?',
        ['1:12', '1:15', '12:1', '15:1'],
        1, 'The stoichiometric (chemically perfect) ratio is approximately 1 part fuel to 15 parts air by weight (1:15 or 15:1 air:fuel).'),

    q('main-metering', 'advanced',
        'If the main air bleed in a float carburetor becomes completely obstructed, the mixture will:',
        ['Lean out at high RPM', 'Become excessively rich at high RPM', 'Cause the engine to quit at idle only', 'Have no effect on the main metering system'],
        1, 'Without the air bleed to break surface tension, fuel flows in large slugs rather than a fine mist — resulting in an excessively rich mixture.'),

    q('fuel-system', 'intermediate',
        'Why is "unusable fuel" determined in the most adverse aircraft attitude?',
        ['To ensure the fuel gauges are calibrated to zero', 'To prevent engine starvation during climbs or slips', 'To account for fuel expansion in hot weather', 'To ensure the tanks can be fully drained during maintenance'],
        1, 'Unusable fuel is tested in the worst-case attitude to ensure the engine will not starve during any normal flight maneuver.'),

    q('hardware-specifics', 'advanced',
        'In a Marvel-Schebler MA-4SPA carburetor, the "Acceleration Well" serves what primary purpose?',
        ['To provide fuel for starting', 'To store a reserve of fuel for rapid throttle advancement', 'To vent the float chamber to the atmosphere', 'To prevent vapor lock in the discharge nozzle'],
        1, 'The acceleration well stores a small quantity of fuel around the main discharge nozzle, providing immediate fuel during rapid throttle opening.'),

    q('gas-laws', 'intermediate',
        'Charles\'s Law states that at a constant pressure, the volume of a gas is:',
        ['Inversely proportional to its absolute temperature', 'Directly proportional to its absolute temperature', 'Equal to the square of its pressure', 'Unaffected by temperature changes'],
        1, 'Charles\'s Law: V₁/T₁ = V₂/T₂. Volume is directly proportional to absolute temperature at constant pressure.'),

    q('troubleshooting', 'advanced',
        'During a troubleshooting check, you find that the engine runs rough at high power but smooth at idle. The most likely cause is:',
        ['Clogged idle jet', 'Restricted main metering orifice', 'Leaking primer valve', 'Incorrect idle speed adjustment'],
        1, 'A restricted main metering jet starves fuel at high power. At idle, the separate idle circuit provides adequate fuel, so idle runs smoothly.'),

    q('fuel-system', 'intermediate',
        'What is the primary function of the "Flow Divider" in a continuous-flow fuel injection system?',
        ['To meter fuel based on air mass', 'To ensure equal distribution of fuel to all cylinders', 'To vaporize fuel before it enters the intake valve', 'To regulate the fuel pressure from the engine-driven pump'],
        1, 'The flow divider distributes metered fuel equally to all cylinders through individual fuel lines and nozzles.'),

    q('pressure-carb', 'advanced',
        'In an RSA fuel control unit, "Chamber B" contains:',
        ['Venturi suction', 'Inlet air pressure (Impact pressure)', 'Metered fuel pressure', 'Unmetered fuel pressure'],
        0, 'In RSA nomenclature, Chamber B contains venturi suction (low pressure from the venturi throat).'),

    q('troubleshooting', 'intermediate',
        'Carburetor throttle ice is most likely to form when the throttle is in what position?',
        ['Full open', 'Partially closed (cruising)', 'Idle', 'Wide open at high altitude'],
        1, 'At partial throttle, the butterfly edge creates maximum disruption to airflow, combined with temperature drop from fuel evaporation — ideal for ice formation.'),

    q('mixture-control', 'advanced',
        'A "Lean Best Power" mixture provides:',
        ['The maximum power possible regardless of fuel flow', 'Maximum power with the minimum fuel flow for that power setting', 'The lowest Cylinder Head Temperature (CHT)', 'The highest Exhaust Gas Temperature (EGT)'],
        1, 'Lean Best Power is on the lean side of peak EGT — it provides the best power output with minimum fuel for that setting.'),

    q('fuel-properties', 'intermediate',
        'The dye used to identify Avgas 100 (not Low Lead) is:',
        ['Blue', 'Red', 'Green', 'Colorless'],
        2, 'Avgas 100: GREEN. Avgas 100LL: BLUE. Avgas 80: RED.'),

    q('float-internals', 'advanced',
        'If a float in a carburetor develops a leak and fills with fuel, the resulting condition will be:',
        ['A lean mixture because the needle valve stays closed', 'A rich mixture because the fuel level in the bowl rises too high', 'Engine starvation at high power settings', 'Improved atomization due to higher pressure'],
        1, 'A leaking float sinks → needle valve stays open → fuel level rises unchecked → excessively rich mixture or flooding.'),

    q('basic-ops', 'intermediate',
        'What physical law explains the operation of a carburetor venturi?',
        ['Newton\'s Third Law', 'Pascal\'s Law', 'Bernoulli\'s Principle', 'Dalton\'s Law'],
        2, 'Bernoulli\'s Principle: as air velocity increases through the venturi restriction, pressure decreases — drawing fuel from the discharge nozzle.'),

    q('mixture-control', 'advanced',
        'What is the purpose of the "Back-Suction" mixture control system?',
        ['It uses a pump to pull fuel back into the tank', 'It reduces the pressure in the float chamber to lean the mixture', 'It increases the pressure at the discharge nozzle', 'It pulls excess air into the induction system'],
        1, 'The back-suction system applies venturi suction to the float bowl vent, reducing bowl pressure and thereby reducing fuel flow for leaning.'),

    q('troubleshooting', 'beginner',
        'Vapor lock is most commonly caused by:',
        ['High fuel pressure and cold temperatures', 'Low fuel pressure and high temperatures', 'Excessively high octane fuel', 'Clogged fuel vents'],
        1, 'Vapor lock occurs when fuel vaporizes in the lines due to a combination of low pressure and high temperature.'),

    q('basic-ops', 'intermediate',
        'The fuel-to-air ratio is calculated based on:',
        ['Volume', 'Weight', 'Pressure', 'Viscosity'],
        1, 'Fuel-to-air ratio is always expressed by WEIGHT (mass), not volume.'),

    q('fuel-system', 'advanced',
        'In a fuel injection system, why are the discharge nozzles vented to the atmosphere or upper deck pressure?',
        ['To prevent fuel from siphoning back into the lines', 'To aid in atomization and prevent a vacuum from forming in the fuel line', 'To cool the nozzles during high-power operation', 'To allow excess fuel to drain overboard'],
        1, 'Venting the nozzles ensures proper fuel atomization and prevents a vacuum that would restrict fuel flow.'),

    q('fuel-system', 'intermediate',
        'A "Wet Wing" fuel system refers to:',
        ['A wing that has been sprayed with anti-ice fluid', 'An integral fuel tank where the wing structure itself is the tank', 'A wing containing multiple rubber bladder cells', 'A wing that uses external pods for fuel storage'],
        1, 'A wet wing (integral tank) is formed by sealing sections of the wing structure itself, making the wing the fuel container.'),

    q('mixture-control', 'advanced',
        'What happens to the fuel-air mixture if the "Automatic Mixture Control" (AMC) bellows ruptures at sea level?',
        ['The mixture becomes excessively rich', 'The mixture becomes excessively lean', 'There is no change at sea level', 'The engine will fail to start'],
        2, 'At sea level, the AMC bellows is fully compressed. A rupture at sea level means no change because the bellows was already in its minimum position.'),

    q('troubleshooting', 'intermediate',
        'Detonation in a reciprocating engine is often caused by:',
        ['A mixture that is too rich', 'Using a fuel with too low an octane rating', 'Low cylinder head temperatures', 'Advanced ignition timing and a rich mixture'],
        1, 'Low-octane fuel has less resistance to knock; the fuel-air charge detonates rather than burning smoothly from the spark front.'),

    q('diagnostics', 'advanced',
        'When adjusting the idle mixture, an RPM rise of more than 50 RPM indicates:',
        ['The idle mixture is too lean', 'The idle mixture is too rich', 'The idle speed is set too high', 'The air bleed is clogged'],
        1, 'More than 50 RPM rise when pulling to cut-off indicates the idle mixture was set too rich. Proper is 25–50 RPM rise.'),

    q('fuel-system', 'intermediate',
        'A fuel pressure gauge in a fuel-injected engine actually measures:',
        ['The pressure of the air in the venturi', 'The fuel pressure at the inlet of the flow divider', 'The volume of fuel in the tanks', 'The pressure in the exhaust manifold'],
        1, 'In most fuel injection systems, the fuel pressure gauge reads the pressure at or near the flow divider inlet — this correlates to fuel flow.'),

    q('mixture-control', 'advanced',
        'The "Power Enrichment" system (Economizer) in a float carburetor is operated by:',
        ['A bellows responding to altitude', 'The position of the throttle valve or manifold pressure', 'The pilot via the mixture control lever', 'The fuel pressure from the boost pump'],
        1, 'The economizer is mechanically linked to the throttle or responds to manifold pressure, opening at high power settings to enrich the mixture.'),

    q('fuel-system', 'intermediate',
        'Which type of fuel tank is secured with snaps or laces?',
        ['Integral tank', 'Rigid aluminum tank', 'Bladder-type tank', 'Terneplate tank'],
        2, 'Bladder (fuel cell) tanks are made of rubberized fabric and are secured inside wing cavities with snaps or lace-cord attachments.'),

    q('fuel-system', 'advanced',
        'Why do fuel-injected engines generally require a more robust priming system than carbureted engines?',
        ['Because fuel is injected directly into the cylinders, skipping the induction path', 'Because there is no fuel in the induction system to vaporize during cranking', 'Because the fuel lines are under higher pressure', 'Because the fuel-to-air ratio is leaner at start'],
        1, 'In a fuel-injected engine, the induction system is dry (no float bowl or standing fuel). The primer must provide fuel directly for starting.'),

    q('basic-ops', 'beginner',
        'What is the purpose of the fuel "Sump"?',
        ['To provide a mounting point for the fuel pump', 'To collect water and sediment at the lowest point of the tank', 'To vent the tank to the atmosphere', 'To store unusable fuel'],
        1, 'The sump is designed as the lowest point to collect water and contaminants for draining during preflight checks.'),

    q('pressure-carb', 'advanced',
        'In a pressure-type carburetor, what force moves the poppet valve toward the open position?',
        ['Air metering force', 'Fuel metering force', 'Spring pressure only', 'Impact air pressure in Chamber A'],
        0, 'The air metering force (difference between impact air and venturi suction on the diaphragm) moves the poppet valve toward open.'),

    q('main-metering', 'intermediate',
        'When an engine is running at idle, the venturi suction is:',
        ['At its maximum', 'Non-existent or too low to draw fuel from the main nozzle', 'Used to operate the acceleration pump', 'Constant regardless of throttle position'],
        1, 'At idle, the throttle is nearly closed — very little air passes through the venturi, so suction is insufficient for the main nozzle.'),

    q('basic-ops', 'advanced',
        'What is the "Inertia Effect" regarding fuel metering?',
        ['The tendency of fuel to stay in the tank during maneuvers', 'The delay in fuel flow compared to air flow during rapid throttle movements', 'The resistance of the float to movement in turbulence', 'The effect of G-loads on fuel pressure'],
        1, 'Fuel, being denser than air, resists changes in speed/direction more — causing a lag during rapid throttle changes (why accelerator pumps exist).'),

    q('fuel-system', 'intermediate',
        'A restricted fuel tank vent will cause:',
        ['The engine to run rich', 'The engine to stop due to a vacuum forming in the tank', 'Fuel to leak from the carburetor', 'An increase in fuel pressure'],
        1, 'Without proper venting, fuel consumption creates a vacuum in the tank that eventually stops fuel flow — causing engine stoppage.'),

    q('fuel-system', 'advanced',
        'In a Teledyne-Continental fuel injection system, what happens to the excess fuel not needed by the engine?',
        ['It is burned in the exhaust', 'It is returned to the inlet side of the engine-driven pump', 'It is stored in a small accumulator', 'It is vented overboard'],
        1, 'Excess fuel is recirculated back to the inlet side of the pump through a return line, maintaining proper system pressure.'),

    q('fuel-properties', 'intermediate',
        'What is the specific gravity of standard Avgas?',
        ['0.60', '0.72', '1.00', '6.00'],
        1, 'Standard Avgas has a specific gravity of approximately 0.72 (lighter than water, which is 1.00).'),

    q('troubleshooting', 'advanced',
        'Which condition would require the most carburetor heat?',
        ['High power, high humidity, air temperature 100°F', 'Low power, high humidity, air temperature 40°F', 'Low power, low humidity, air temperature 10°F', 'High power, low humidity, air temperature 60°F'],
        1, 'Low power + high humidity + 40°F OAT is the most dangerous combination for carb ice. The 20–70°F range with moisture is the critical zone.'),

    q('fuel-system', 'intermediate',
        'The purpose of the "Finger Strainer" is to:',
        ['Prevent large debris from entering the fuel lines at the tank outlet', 'Filter the fuel before it enters the discharge nozzle', 'Catch water in the gascolator', 'Measure the fuel flow rate'],
        0, 'The finger screen/strainer is a coarse filter at the tank outlet that catches large debris before it enters the fuel lines.'),

    q('carb-parts', 'advanced',
        'What is the primary disadvantage of a "Float-Type" carburetor?',
        ['It is too heavy', 'It is susceptible to icing and is affected by aircraft attitude', 'It cannot be used on engines over 100 HP', 'It requires a high-pressure fuel pump'],
        1, 'Float carburetors are prone to icing from fuel evaporation and are affected by maneuvers (G-loads change float position).'),

    q('fuel-properties', 'intermediate',
        'Reid Vapor Pressure refers to:',
        ['The pressure at which fuel turns to ice', 'The volatility of the fuel at 100°F', 'The pressure required to pump fuel to the engine', 'The resistance of fuel to detonation'],
        1, 'Reid Vapor Pressure (RVP) measures fuel volatility at 100°F — higher RVP means the fuel vaporizes more readily.'),

    q('mixture-control', 'advanced',
        'A "Variable Orifice" mixture control works by:',
        ['Changing the size of the main air bleed', 'Changing the size of the fuel passage to the main discharge nozzle', 'Restricting the flow of air into the venturi', 'Adjusting the float level mechanically'],
        1, 'A variable orifice (rotary or needle) mixture control physically changes the size of the fuel passage to regulate fuel flow.'),

    q('troubleshooting', 'intermediate',
        '"Impact Icing" occurs when:',
        ['Fuel vaporizes in the venturi', 'Moisture in the air hits the air filter or intake duct and freezes', 'The throttle valve is cooled by low pressure', 'Water in the fuel freezes in the lines'],
        1, 'Impact ice forms when visible moisture (rain, fog) strikes cold surfaces in the intake system and freezes on contact.'),

    q('diagnostics', 'advanced',
        'Why must the engine be at operating temperature before adjusting the idle mixture?',
        ['To ensure the oil is thin enough', 'To ensure proper fuel vaporization and consistent combustion', 'To prevent the battery from draining', 'To allow the metal parts of the carburetor to expand'],
        1, 'Cold engines don\'t vaporize fuel properly, giving false readings. Operating temp ensures consistent, repeatable mixture adjustment.'),

    q('mixture', 'intermediate',
        'A fuel-air mixture ratio of 1:11 is considered:',
        ['Lean', 'Rich', 'Stoichiometric', 'Ideal for economy'],
        1, '1:11 means more fuel per unit of air than the stochiometric 1:15. More fuel = richer mixture.'),

    q('pressure-carb', 'advanced',
        'In an RSA fuel injection system, what provides the force to close the poppet valve?',
        ['Venturi suction in Chamber B', 'Metered fuel pressure in Chamber C', 'Unmetered fuel pressure in Chamber D', 'The idle spring and fuel pressure in Chamber D'],
        3, 'The idle spring and unmetered fuel pressure in Chamber D provide the closing force on the poppet valve.'),

    q('mixture-control', 'intermediate',
        'What is the function of the "Economizer" air bleed?',
        ['To save fuel at high power settings', 'To provide a rich mixture for cooling at high power settings', 'To lean the mixture during cruise', 'To prevent ice formation'],
        1, 'The power enrichment/economizer provides EXTRA fuel at high power for cylinder cooling and detonation prevention.'),

    q('troubleshooting', 'advanced',
        'If an engine "backfires" through the induction system when the throttle is advanced quickly, the most likely cause is:',
        ['An excessively rich mixture', 'A defective accelerator pump', 'A clogged main air bleed', 'A leaking primer valve'],
        1, 'A defective accelerator pump fails to provide the extra fuel needed during rapid throttle advance → momentary lean condition → backfire.'),

    q('fuel-properties', 'beginner',
        'What color is Avgas 80?',
        ['Red', 'Blue', 'Green', 'Brown'],
        0, 'Avgas 80 is dyed RED. Avgas 100 = GREEN. Avgas 100LL = BLUE.'),

    q('fuel-system', 'advanced',
        'A "Full Authority Digital Engine Control" (FADEC) system eliminates the need for:',
        ['Fuel tanks', 'Mixture and throttle control linkages', 'Spark plugs', 'Induction air'],
        1, 'FADEC replaces manual mixture and throttle cables with electronic control — the computer manages fuel metering and ignition timing.'),

    q('mixture-control', 'intermediate',
        'The "Idle Cut-Off" position on a mixture control:',
        ['Slows the engine to 500 RPM', 'Completely stops the flow of fuel from the metering device', 'Turns off the ignition switch', 'Diverts fuel back to the tanks'],
        1, 'Idle Cut-Off completely blocks fuel flow through the mixture control valve, starving the engine for a clean shutdown.'),

    q('diagnostics', 'advanced',
        'In an RSA system, if the "Main Metering Jet" is partially clogged, the EGT will likely:',
        ['Decrease on all cylinders', 'Increase on all cylinders (showing a lean condition)', 'Increase on only one cylinder', 'Stay the same but the engine will lose RPM'],
        1, 'A clogged main jet restricts fuel → lean mixture → higher combustion temps → EGT rises on ALL cylinders.'),

    q('fuel-system', 'intermediate',
        'What is the main benefit of a "Crossfeed" fuel system?',
        ['It allows the pilot to jettison fuel', 'It allows any engine to draw fuel from any tank', 'It increases the total fuel capacity', 'It prevents water from entering the engines'],
        1, 'Crossfeed allows fuel from one side to feed engines on the other side — critical if one engine fails or a tank develops a problem.'),

    q('basic-ops', 'advanced',
        'What is the "Critical Altitude" for a normally aspirated engine?',
        ['The altitude where carburetor ice is most likely', 'The altitude where the throttle is wide open to maintain rated power', 'Sea level', '10,000 feet'],
        1, 'Critical altitude is where the throttle must be fully open to produce rated power — above this, power decreases regardless of throttle position.'),

    q('basic-ops', 'intermediate',
        'The term "Specific Fuel Consumption" (SFC) is defined as:',
        ['Gallons per hour', 'Pounds of fuel per hour per horsepower', 'The octane rating divided by the fuel weight', 'The ratio of fuel to air by volume'],
        1, 'SFC = lb/hr/HP. It measures how efficiently the engine converts fuel into power.'),

    q('diagnostics', 'advanced',
        'Which component of a float carburetor is most likely to be affected by "Springback" during maintenance?',
        ['The float needle valve', 'The throttle and mixture control linkages', 'The main metering jet', 'The air filter'],
        1, 'Springback is the extra cockpit control travel after the carburetor internal stops are reached. It affects throttle and mixture cables/linkages.'),

    q('pressure-carb', 'intermediate',
        'A "Pressure-Injection" carburetor differs from a "Float" carburetor because:',
        ['It has no venturi', 'It uses a pressurized fuel system and a closed fuel chamber instead of a vented float bowl', 'It only works on radial engines', 'It does not require a mixture control'],
        1, 'A pressure carburetor uses a sealed fuel chamber with a diaphragm system — no float, bowl, or atmospheric venting needed.'),

    q('troubleshooting', 'advanced',
        'If the fuel level in a float carburetor is too low, the engine will likely:',
        ['Run rich at all speeds', 'Run lean or hesitate during acceleration', 'Backfire through the exhaust', 'Fail to stop in Idle Cut-Off'],
        1, 'Low fuel level means fuel must be lifted farther before it can discharge, requiring more suction — resulting in a lean condition, especially during acceleration.'),

    q('fuel-system', 'intermediate',
        'What is the purpose of "Static Bonding" in a fuel system?',
        ['To hold the fuel lines in place', 'To prevent the buildup of static electricity during refueling', 'To provide a return path for the ignition system', 'To strengthen the fuel tanks'],
        1, 'Static bonding electrically connects all fuel system components to dissipate static charges that could cause sparks during refueling.'),

    q('fuel-system', 'advanced',
        'A "Continuous Flow" fuel injection system:',
        ['Injects fuel only when the intake valve is open', 'Injects fuel into the intake port at all times the engine is running', 'Uses a high-pressure pump to blast fuel into the combustion chamber', 'Does not require a fuel control unit'],
        1, 'Continuous flow systems (like RSA and Continental) spray fuel continuously at the intake port — not timed to valve events.'),

    q('basic-ops', 'intermediate',
        'The "mixture" refers to the ratio of:',
        ['Fuel to oil', 'Fuel to air', 'Avgas to Jet-A', 'Water to methanol'],
        1, 'The mixture is the ratio of fuel to air by weight entering the engine for combustion.'),

    q('fuel-system', 'advanced',
        'When a fuel injection nozzle is "Turbocharged," it is vented to:',
        ['Ambient air', 'Upper deck pressure (Compressor discharge pressure)', 'Exhaust back pressure', 'Vacuum'],
        1, 'Turbocharged nozzles are vented to upper deck pressure (turbo compressor discharge) to account for the higher induction system pressure.'),

    q('troubleshooting', 'intermediate',
        'Why does a lean mixture cause higher cylinder head temperatures?',
        ['It contains more energy', 'It burns slower, exposing the cylinder walls to heat for a longer duration', 'It burns faster and hotter than a rich mixture', 'It produces more carbon deposits'],
        1, 'A lean mixture burns slower — the flame front takes longer to traverse the cylinder, exposing the metal to high temperatures for a longer period.'),

    q('hardware-specifics', 'advanced',
        'In a Marvel-Schebler carburetor, the "Idle Mixture Screw" controls:',
        ['The amount of air entering the idle system', 'The amount of fuel entering the idle system', 'The throttle opening at idle', 'The float level'],
        1, 'The idle mixture screw is a needle valve that controls the FUEL flow through the idle circuit.'),

    q('fuel-system', 'intermediate',
        'What is the purpose of the "Scupper" drain?',
        ['To drain the fuel tanks', 'To catch spilled fuel at the filler neck and drain it overboard', 'To drain water from the gascolator', 'To vent the carburetor'],
        1, 'The scupper is a collection area around the fuel filler neck that catches overflow and directs it harmlessly overboard.'),

    q('pressure-carb', 'advanced',
        'If the "Constant Effort Spring" in a fuel injection system breaks, the mixture will:',
        ['Stay at full rich', 'Become leaner at low power settings', 'Prevent the engine from starting', 'Cause a high-speed surge'],
        1, 'The constant effort spring provides a minimum fuel-air ratio at low airflows. Without it, the poppet valve can\'t provide enough fuel at idle — lean.'),

    q('troubleshooting', 'intermediate',
        '"Preignition" is caused by:',
        ['A mixture that is too lean', 'A hot spot in the cylinder igniting the fuel before the spark plug fires', 'High octane fuel', 'Retarded ignition timing'],
        1, 'Preignition occurs when a red-hot carbon deposit, sharp edge, or other hot spot ignites the mixture BEFORE the spark plug fires.'),

    q('pressure-carb', 'advanced',
        'What is the "Metered Fuel Pressure" in an RSA system?',
        ['The pressure before the main metering jet', 'The pressure after the main metering jet but before the discharge nozzles', 'The pressure at the fuel pump', 'The pressure in the fuel tank'],
        1, 'Metered fuel pressure is measured AFTER the fuel has passed through the main metering jet but BEFORE it reaches the nozzles.'),

    q('fuel-system', 'intermediate',
        'What is the function of the "Anti-Detonant Injection" (ADI) system?',
        ['To allow the engine to produce more power by cooling the cylinders with a water-alcohol mix', 'To clean the spark plugs', 'To prevent vapor lock', 'To lubricate the valves'],
        0, 'ADI injects a water-methanol mixture to suppress detonation during high-power operations like takeoff, enabling higher manifold pressures.'),

    q('carb-parts', 'advanced',
        'A "Double-Float" carburetor is typically used on:',
        ['Small trainers like the Cessna 150', 'Large radial engines to prevent fuel starvation during maneuvers', 'Turbocharged engines only', 'Helicopters'],
        1, 'Double-float designs are used on larger engines to maintain consistent fuel level during maneuvers, especially on radials.'),

    q('fuel-system', 'intermediate',
        'The "Gascolator" is usually located:',
        ['At the highest point in the fuel system', 'At the lowest point in the fuel system, before the carburetor', 'Inside the fuel tank', 'On the exhaust manifold'],
        1, 'The gascolator is at the system\'s lowest point to trap water (which is heavier than fuel) and sediment before they reach the carburetor.'),

    q('diagnostics', 'advanced',
        'If an RSA fuel control unit has a "Rich" shift in the mid-range, the most likely cause is:',
        ['A clogged air bleed', 'A worn throttle shaft or internal linkage', 'High fuel pump pressure', 'A leaking fuel nozzle'],
        1, 'A worn throttle shaft allows air to bypass the metered path, changing the air-to-fuel ratio at certain throttle positions.'),

    q('fuel-properties', 'intermediate',
        'Why is "Tetraethyl Lead" (TEL) added to Avgas?',
        ['To give the fuel its color', 'To increase the octane rating and prevent detonation', 'To make the fuel heavier', 'To prevent the fuel from freezing'],
        1, 'TEL increases octane (anti-knock) properties and also provides upper cylinder lubrication for exhaust valve seats.'),

    q('pressure-carb', 'advanced',
        '"Air Metering Force" is the difference between:',
        ['Impact air pressure and venturi suction', 'Atmospheric pressure and fuel pressure', 'Unmetered fuel and metered fuel', 'Throttle pressure and manifold pressure'],
        0, 'Air metering force = Impact air pressure minus venturi suction. This differential acts on the diaphragm to open the poppet valve.'),

    q('carb-parts', 'intermediate',
        'A "Downdraft" carburetor is mounted:',
        ['Below the engine', 'On the side of the engine', 'Above the engine', 'Inside the intake manifold'],
        2, 'A downdraft carburetor sits on top (above) the engine so air flows downward through the venturi and into the intake manifold.'),

    q('carb-parts', 'advanced',
        'The "Safety Throttle Spring" is designed to:',
        ['Close the throttle if the cable breaks', 'Open the throttle to a cruise setting if the cable breaks', 'Prevent the throttle from moving too fast', 'Lock the throttle during takeoff'],
        1, 'The safety spring holds the throttle partially open so the engine continues to produce some power if the cable/linkage fails.'),

    q('fuel-properties', 'intermediate',
        'What does the "Octane Number" represent?',
        ['The percentage of lead in the fuel', 'The fuel\'s resistance to "knock" or detonation', 'The energy content in BTUs', 'The weight of the fuel per gallon'],
        1, 'The octane number rates a fuel\'s ability to resist detonation (knocking). Higher octane = more resistance.'),

    q('hardware-specifics', 'advanced',
        'In a Continental fuel injection system, the "Manual Mixture Control" valve:',
        ['Vents fuel pressure to the atmosphere', 'Meters fuel by rotating a shaft with a contoured flat', 'Controls the air entering the venturi', 'Adjusts the pump relief valve'],
        1, 'Continental systems use a rotary valve with a contoured flat that progressively uncovers the fuel return port to lean the mixture.'),

    q('fuel-properties', 'intermediate',
        '"Specific Gravity" is measured using a:',
        ['Barometer', 'Hydrometer', 'Altimeter', 'Thermometer'],
        1, 'A hydrometer measures the specific gravity (density relative to water) of liquids like fuel.'),

    q('fuel-system', 'advanced',
        'Why do some fuel nozzles have "Shrouds"?',
        ['To protect them from physical damage', 'To provide a path for cooling air or "upper deck" air to aid atomization', 'To keep them from leaking', 'To reduce noise'],
        1, 'Nozzle shrouds channel air around the nozzle tip to aid in breaking up the fuel stream for better atomization.'),

    q('fuel-system', 'intermediate',
        'A "Fuel Jettison" system is required on aircraft if:',
        ['They have more than two engines', 'The maximum takeoff weight exceeds the maximum landing weight', 'They fly over the ocean', 'They use Jet-A fuel'],
        1, 'If max takeoff weight > max landing weight, the aircraft must have a way to dump fuel to reach safe landing weight in emergencies.'),

    q('pressure-carb', 'advanced',
        '"Fuel Metering Force" in a pressure carburetor is the difference between:',
        ['Chambers A and B', 'Chambers C and D', 'Chambers A and C', 'Chambers B and D'],
        1, 'Fuel metering force = unmetered fuel pressure (Chamber D) minus metered fuel pressure (Chamber C). This force acts to close the poppet valve.'),

    q('troubleshooting', 'intermediate',
        '"Afterfiring" in the exhaust is usually caused by:',
        ['An excessively lean mixture', 'An excessively rich mixture or a leaking primer', 'Cold spark plugs', 'High altitude'],
        1, 'Afterfiring happens when unburned fuel (from a rich condition or leaking primer) enters the exhaust and ignites from the heat.'),

    q('diagnostics', 'advanced',
        'When a carburetor is "Bench Flowed," it is checked for:',
        ['Leaks only', 'Fuel-to-air ratio accuracy at various simulated airflow rates', 'The weight of the float', 'The color of the paint'],
        1, 'Bench flowing tests the carb on a flow bench at various airflow rates to verify the fuel-air ratio meets specifications.'),

    q('main-metering', 'intermediate',
        'The "Main Discharge Nozzle" is located:',
        ['At the entrance to the air filter', 'At the narrowest part of the venturi', 'In the cylinder head', 'Behind the throttle valve'],
        1, 'The main discharge nozzle is positioned at the venturi throat (narrowest point) where the lowest pressure exists.'),

    q('carb-parts', 'advanced',
        'A "Step" in the carburetor venturi is used to:',
        ['Increase the speed of the air', 'Create a local low-pressure area for the air bleed', 'Prevent ice', 'Strengthen the carburetor body'],
        1, 'The step (a small ledge/notch in the venturi wall) creates a localized low-pressure area that helps the air bleed function properly.'),

    q('fuel-properties', 'intermediate',
        'Why is it dangerous to use a fuel with a lower octane rating than specified?',
        ['The engine will not start', 'It will cause detonation and potential engine failure', 'It will clog the filters', 'It will cause the fuel to evaporate too slowly'],
        1, 'Low-octane fuel detonates prematurely under the engine\'s compression and heat — leading to destructive pressure spikes.'),

    q('diagnostics', 'advanced',
        'In an RSA system, if the "Impact Tubes" are pointed away from the airflow, the mixture will:',
        ['Become very rich', 'Become very lean', 'Not change', 'Cause the engine to overspeed'],
        1, 'Misaligned impact tubes reduce the sensed impact pressure → lower air metering force → less fuel delivered → lean mixture.'),

    q('fuel-system', 'intermediate',
        'The "Primer" system is used to:',
        ['Clean the engine', 'Provide extra fuel for starting a cold engine', 'Lubricate the cylinders', 'Extinguish engine fires'],
        1, 'The primer injects raw fuel directly into the intake ports or cylinders to provide the rich mixture needed for cold starting.'),

    q('diagnostics', 'advanced',
        'A "Fuel Pressure Discharge" valve (as seen in RSA units) ensures:',
        ['Fuel pressure is always 50 psi', 'The fuel lines stay full of fuel when the engine is shut down', 'Fuel flow stops immediately when the mixture is pulled to ICO', 'Vapor lock is eliminated'],
        2, 'The discharge valve ensures a positive shutoff when the mixture is pulled to idle cut-off, preventing fuel from siphoning to the nozzles.'),

    q('fuel-properties', 'intermediate',
        'What is "100LL" Avgas?',
        ['100 Octane, High Lead', '100 Octane, Low Lead', '100 Octane, Liquid Lead', '100 Octane, Lead Limited'],
        1, '100LL = 100 Octane, Low Lead. It has the same octane as Avgas 100 but with reduced lead content. Dyed blue.'),

    q('diagnostics', 'advanced',
        'The "Idle Mixture" on a float carburetor is adjusted by:',
        ['Changing the size of the idle jet', 'Turning a needle valve that restricts fuel or air in the idle circuit', 'Adjusting the throttle stop', 'Bending the float arm'],
        1, 'The idle mixture screw is a needle valve that controls the amount of fuel (or air, in some designs) flowing through the idle circuit.'),

    q('gas-laws', 'intermediate',
        '"Boyle\'s Law" is most relevant to which engine system?',
        ['The electrical system', 'The induction and compression strokes', 'The landing gear', 'The cabin lighting'],
        1, 'Boyle\'s Law (P × V = constant at constant temp) directly applies to induction and compression — as piston compresses, pressure rises.'),

    q('fuel-system', 'advanced',
        'If the fuel return line to the tank is restricted in a Teledyne-Continental system:',
        ['The fuel flow will decrease', 'The fuel pressure and flow to the nozzles will increase (rich mixture)', 'The engine will run lean', 'The boost pump will burn out'],
        1, 'A restricted return line means less fuel can escape back to the tank → higher pressure at the nozzles → richer mixture.'),

    q('basic-ops', 'intermediate',
        'What is the "Venturi Effect"?',
        ['High pressure creating heat', 'Low pressure created by increased fluid velocity in a restriction', 'The tendency of fuel to evaporate', 'The sound the engine makes at idle'],
        1, 'The venturi effect is the pressure drop that occurs when fluid velocity increases as it passes through a constriction.'),

    q('pressure-carb', 'advanced',
        'In an RSA fuel control, the "Constant Head Idle Spring" provides:',
        ['Fuel pressure at high power', 'A constant fuel-air ratio during idling when air forces are too weak', 'Tension for the throttle cable', 'Acceleration fuel'],
        1, 'At idle, airflow forces are too small to properly position the poppet valve. The idle spring provides supplemental force for proper metering.'),

    q('main-metering', 'intermediate',
        'The "Main Metering Jet" is sized to provide:',
        ['The best economy mixture at all times', 'The maximum required fuel for the engine at full power', 'Only the idle fuel', 'The fuel for starting'],
        1, 'The main metering jet is calibrated to deliver the maximum fuel flow needed at full-power operation.'),

    q('troubleshooting', 'advanced',
        'If an engine runs well at high power but "hunts" or surges at idle, the most likely cause is:',
        ['An air leak in the induction manifold', 'A clogged main jet', 'High fuel octane', 'A dirty air filter'],
        0, 'An induction leak adds unmetered air at low speeds (idle), upsetting the mixture and causing hunting/surging. At high power, the leak is insignificant.'),

    q('fuel-system', 'intermediate',
        'What is the purpose of "Fuel Crossfeed"?',
        ['To allow fuel to flow between the left and right tanks', 'To allow an engine to receive fuel from a tank on the opposite side', 'To mix different types of fuel', 'To jettison fuel in an emergency'],
        1, 'Crossfeed allows an engine to be fed from the opposite wing\'s tank — essential for balancing fuel loads or if one engine fails.'),

    q('fuel-system', 'advanced',
        'Why is "Water Injection" used on some high-performance reciprocating engines?',
        ['To prevent the fuel from freezing', 'To suppress detonation and allow higher manifold pressures for takeoff', 'To clean the exhaust valves', 'To increase the fuel\'s octane rating permanently'],
        1, 'Water/methanol injection absorbs heat during vaporization, cooling the charge and suppressing detonation at high manifold pressures.'),

    q('basic-ops', 'intermediate',
        'What is the "Stoichiometric" mixture ratio for gasoline?',
        ['15 parts air to 1 part fuel', '12 parts air to 1 part fuel', '8 parts air to 1 part fuel', '20 parts air to 1 part fuel'],
        0, 'The stoichiometric ratio for gasoline is approximately 15:1 (air to fuel by weight) for complete combustion.'),

    q('fuel-system', 'advanced',
        'A "Vapor Vent" in a fuel injection pump:',
        ['Allows fuel vapor to return to the tank to prevent vapor lock', 'Vents fuel to the cockpit', 'Controls the idle speed', 'Is used for priming only'],
        0, 'The vapor vent returns fuel vapors to the fuel tank, maintaining liquid fuel at the pump inlet and preventing vapor lock.'),
];

// ===== CATEGORY MAP =====
export const CATEGORIES = {
    'all': 'All Topics',
    'basic-ops': 'Basic Operations',
    'main-metering': 'Main Metering & Idling',
    'mixture-control': 'Mixture Control & Economizers',
    'pressure-carb': 'Pressure Carburetors',
    'troubleshooting': 'Troubleshooting',
    'fuel-properties': 'Fuel Properties',
    'float-internals': 'Float Internals',
    'diagnostics': 'Diagnostics',
    'carb-parts': 'Carburetor Components',
    'fuel-system': 'Fuel System',
    'mixture': 'Mixture & Metering',
    'processes': 'Operations & Processes',
    'gas-laws': 'Gas Laws & Powerplant Math',
    'hardware-specifics': 'Hardware Specifics (MSA)',
};

// ===== ANALYTICS STORAGE =====
function getStorageKey() {
    const id = localStorage.getItem('carb-current-user-id');
    return id ? `carb-quiz-analytics_${id}` : 'carb-quiz-analytics';
}

function loadAnalytics() {
    try {
        const key = getStorageKey();
        const data = localStorage.getItem(key);
        return data ? JSON.parse(data) : {
            attempts: [],       // { date, category, difficulty, score, total, questions: [{id, correct, time}] }
            questionStats: {},  // { [id]: { seen, correct, lastSeen, streak } }
            xp: 0,
            level: 1,
            streakDays: 0,
            lastActiveDate: null,
            totalCorrect: 0,
            totalAnswered: 0,
        };
    } catch { return { attempts: [], questionStats: {}, xp: 0, level: 1, streakDays: 0, lastActiveDate: null, totalCorrect: 0, totalAnswered: 0 }; }
}

function saveAnalytics(data) {
    try { 
        const key = getStorageKey();
        localStorage.setItem(key, JSON.stringify(data)); 
    } catch {}
}

// ===== QUIZ STATE =====
let currentQuiz = [];
let currentIndex = 0;
let score = 0;
let quizStartTime = 0;
let questionStartTime = 0;
let analytics = loadAnalytics();
let currentAttempt = null;
let hintLevel = 0;

// Exported way to refresh analytics when user changes
export function initQuizForUser() {
    analytics = loadAnalytics();
}

// ===== SPACED REPETITION =====
// Questions the user has gotten wrong get inserted more frequently
function getWeightedPool(pool) {
    const weighted = [];
    for (const q of pool) {
        const stat = analytics.questionStats[q.id];
        let weight = 1;
        if (stat) {
            if (stat.streak >= 3) weight = 0.3;     // well-known
            else if (stat.streak >= 1) weight = 0.6; // moderately known
            else if (stat.correct < stat.seen) weight = 2.5; // more wrong than right
            if (stat.streak === 0 && stat.seen > 0) weight = 3; // got it wrong last time
        } else {
            weight = 1.5; // unseen questions get slightly higher weight
        }
        // Push question multiple times based on weight
        const copies = Math.max(1, Math.round(weight * 2));
        for (let i = 0; i < copies; i++) weighted.push(q);
    }
    return weighted;
}

// ===== PUBLIC API =====

export function startQuiz(category = 'all', difficulty = 'all', count = 10) {
    let pool;
    if (category === 'all') {
        pool = [...QUESTIONS];
    } else {
        pool = QUESTIONS.filter(q => q.category === category);
    }

    if (difficulty !== 'all') {
        pool = pool.filter(q => q.difficulty === difficulty);
    }

    if (pool.length === 0) {
        console.warn('No questions found for selection:', category, difficulty);
        return false;
    }

    // Use spaced repetition weighting
    const weighted = getWeightedPool(pool);

    // Shuffle weighted pool
    for (let i = weighted.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [weighted[i], weighted[j]] = [weighted[j], weighted[i]];
    }

    // Deduplicate (keep first occurrence)
    const seen = new Set();
    const deduped = [];
    for (const q of weighted) {
        if (!seen.has(q.id)) {
            seen.add(q.id);
            deduped.push(q);
        }
    }

    // Map questions to a new pool where OPTIONS are shuffled
    currentQuiz = deduped.slice(0, Math.min(count, deduped.length)).map(origQ => {
        // Create a deep-ish copy (clone options so we don't mutate the db)
        const q = { ...origQ, options: [...origQ.options] };
        
        // Keep track of where the original indices went so we can report back to analytics correctly
        const mapping = q.options.map((_, i) => i);
        
        // Fisher-Yates shuffle options and mapping together
        for (let i = q.options.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [q.options[i], q.options[j]] = [q.options[j], q.options[i]];
            [mapping[i], mapping[j]] = [mapping[j], mapping[i]];
        }
        
        // Update correct index
        q.correct = mapping.indexOf(origQ.correct);
        q.originalMapping = mapping; // Save this for submission
        return q;
    });

    currentIndex = 0;
    score = 0;
    hintLevel = 0;
    quizStartTime = Date.now();
    questionStartTime = Date.now();

    currentAttempt = {
        date: new Date().toISOString(),
        category,
        difficulty,
        score: 0,
        total: currentQuiz.length,
        questions: [],
    };

    // Update streak
    updateStreak();

    return { total: currentQuiz.length, current: 0 };
}

function updateStreak() {
    const today = new Date().toDateString();
    if (analytics.lastActiveDate === today) return;

    const yesterday = new Date(Date.now() - 86400000).toDateString();
    if (analytics.lastActiveDate === yesterday) {
        analytics.streakDays++;
    } else if (analytics.lastActiveDate !== today) {
        analytics.streakDays = 1;
    }
    analytics.lastActiveDate = today;
    saveAnalytics(analytics);
}

export function getCurrentQuestion() {
    if (currentIndex >= currentQuiz.length) return null;
    const q = currentQuiz[currentIndex];
    questionStartTime = Date.now();
    hintLevel = 0;
    return {
        number: currentIndex + 1,
        total: currentQuiz.length,
        question: q.question,
        options: q.options,
        category: q.category,
        difficulty: q.difficulty,
        id: q.id,
        relatedPart: q.relatedPart || null,
    };
}

export function getHint() {
    const q = currentQuiz[currentIndex];
    if (!q) return null;

    hintLevel++;

    if (hintLevel === 1) {
        // Textual hint — extract a conceptual clue from the explanation
        const words = q.explanation.split(' ');
        const hintText = words.slice(0, Math.min(15, Math.floor(words.length / 3))).join(' ') + '...';
        return { level: 1, text: `💡 Hint: ${hintText}`, relatedPart: q.relatedPart };
    }

    if (hintLevel >= 2) {
        // Visual hint — identify the region
        return { level: 2, text: '🔶 Look for the warm orange glow on the 3D model...', relatedPart: q.relatedPart };
    }

    return null;
}

export function submitAnswer(optionIndex) {
    const q = currentQuiz[currentIndex];
    const isCorrect = optionIndex === q.correct;
    const timeTaken = Date.now() - questionStartTime;

    if (isCorrect) {
        score++;
        // XP: more for harder questions, less if hints were used
        let xpGain = q.difficulty === 'advanced' ? 30 : q.difficulty === 'intermediate' ? 20 : 10;
        if (hintLevel > 0) xpGain = Math.floor(xpGain * 0.6);
        if (timeTaken < 10000) xpGain += 5; // speed bonus
        analytics.xp += xpGain;
        analytics.totalCorrect++;
    }
    analytics.totalAnswered++;

    // Update question stats
    if (!analytics.questionStats[q.id]) {
        analytics.questionStats[q.id] = { seen: 0, correct: 0, lastSeen: null, streak: 0 };
    }
    const stat = analytics.questionStats[q.id];
    stat.seen++;
    stat.lastSeen = new Date().toISOString();
    if (isCorrect) {
        stat.correct++;
        stat.streak++;
    } else {
        stat.streak = 0;
    }

    // Use mapping to find the original index for history/analytics
    const originalChosenIndex = q.originalMapping ? q.originalMapping[optionIndex] : optionIndex;

    if (currentAttempt) {
        currentAttempt.questions.push({ 
            id: q.id, 
            correct: isCorrect, 
            time: timeTaken, 
            hintsUsed: hintLevel, 
            chosenIndex: originalChosenIndex 
        });
        currentAttempt.score = score;
    }

    // Level up check
    const newLevel = Math.floor(analytics.xp / 100) + 1;
    const leveledUp = newLevel > analytics.level;
    analytics.level = newLevel;

    saveAnalytics(analytics);

    return {
        correct: isCorrect,
        correctIndex: q.correct,
        explanation: q.explanation,
        score,
        total: currentQuiz.length,
        xpGained: isCorrect ? (q.difficulty === 'advanced' ? 30 : q.difficulty === 'intermediate' ? 20 : 10) : 0,
        leveledUp,
        level: analytics.level,
        relatedPart: q.relatedPart,
        difficulty: q.difficulty,
        timeTaken,
    };
}

export function nextQuestion() {
    currentIndex++;
    if (currentIndex >= currentQuiz.length) {
        // Quiz complete — save attempt
        if (currentAttempt) {
            analytics.attempts.push(currentAttempt);
            // Keep max 50 attempts
            if (analytics.attempts.length > 50) analytics.attempts = analytics.attempts.slice(-50);
            saveAnalytics(analytics);
            currentAttempt = null;
        }
        return null;
    }
    return getCurrentQuestion();
}

export function getScore() {
    return { score, total: currentQuiz.length };
}

// ===== ANALYTICS API =====

export function getAnalytics() {
    analytics = loadAnalytics();
    return {
        xp: analytics.xp,
        level: analytics.level,
        streakDays: analytics.streakDays,
        totalCorrect: analytics.totalCorrect,
        totalAnswered: analytics.totalAnswered,
        accuracy: analytics.totalAnswered > 0 ? Math.round((analytics.totalCorrect / analytics.totalAnswered) * 100) : 0,
        attempts: analytics.attempts,
    };
}

export function getTopicMastery() {
    const topics = {};
    for (const q of QUESTIONS) {
        if (!topics[q.category]) topics[q.category] = { total: 0, correct: 0 };
        topics[q.category].total++;
        const stat = analytics.questionStats[q.id];
        if (stat && stat.correct > 0) {
            topics[q.category].correct += Math.min(stat.correct / stat.seen, 1);
        }
    }

    const result = {};
    for (const [cat, data] of Object.entries(topics)) {
        result[cat] = {
            name: CATEGORIES[cat] || cat,
            mastery: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0,
        };
    }
    return result;
}

export function getDifficultyPerformance() {
    const diffs = { beginner: { total: 0, correct: 0 }, intermediate: { total: 0, correct: 0 }, advanced: { total: 0, correct: 0 } };

    for (const attempt of analytics.attempts) {
        for (const aq of attempt.questions) {
            const question = QUESTIONS.find(q => q.id === aq.id);
            if (question) {
                diffs[question.difficulty].total++;
                if (aq.correct) diffs[question.difficulty].correct++;
            }
        }
    }

    return {
        beginner: diffs.beginner.total > 0 ? Math.round((diffs.beginner.correct / diffs.beginner.total) * 100) : 0,
        intermediate: diffs.intermediate.total > 0 ? Math.round((diffs.intermediate.correct / diffs.intermediate.total) * 100) : 0,
        advanced: diffs.advanced.total > 0 ? Math.round((diffs.advanced.correct / diffs.advanced.total) * 100) : 0,
    };
}

export function getLearningProgression() {
    return analytics.attempts.map((a, i) => ({
        attempt: i + 1,
        accuracy: a.total > 0 ? Math.round((a.score / a.total) * 100) : 0,
        date: a.date,
        category: a.category,
    }));
}

export function getQuestionCount(category = 'all', difficulty = 'all') {
    let pool = [...QUESTIONS];
    if (category !== 'all') pool = pool.filter(q => q.category === category);
    if (difficulty !== 'all') pool = pool.filter(q => q.difficulty === difficulty);
    return pool.length;
}

export function resetAnalytics() {
    analytics = { attempts: [], questionStats: {}, xp: 0, level: 1, streakDays: 0, lastActiveDate: null, totalCorrect: 0, totalAnswered: 0 };
    saveAnalytics(analytics);
}

// Returns wrong answers from the last completed attempt for review
export function getWrongAnswers() {
    // Use the most recent completed attempt
    const attempt = analytics.attempts.length > 0
        ? analytics.attempts[analytics.attempts.length - 1]
        : currentAttempt;
    if (!attempt || !attempt.questions) return [];

    const wrongs = [];
    for (const qa of attempt.questions) {
        if (!qa.correct) {
            const question = QUESTIONS.find(q => q.id === qa.id);
            if (question) {
                wrongs.push({
                    id: question.id,
                    question: question.question,
                    yourAnswer: qa.chosenIndex !== undefined ? question.options[qa.chosenIndex] : '—',
                    correctAnswer: question.options[question.correct],
                    explanation: question.explanation,
                    category: question.category,
                    difficulty: question.difficulty,
                });
            }
        }
    }
    return wrongs;
}
